import { useState, useEffect } from 'react';
import BookDemoModal from './BookDemoModal';
import { useTheme } from '@/hooks/useTheme';

export default function HeroSection() {
  const [modalOpen, setModalOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const { theme } = useTheme();

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const isDark = theme === 'dark';

  return (
    <>
      <section className={`relative min-h-screen flex flex-col overflow-hidden transition-colors duration-500 ${isDark ? 'bg-[#060A14]' : 'bg-[#F8FAFC]'}`}>
        {/* Full-bleed background image */}
        <div className="absolute inset-0">
          <img
            src={isDark 
              ? "https://public.readdy.ai/ai/img_res/4413a021f03779d9fe23efc6697015c4.jpg"
              : "https://readdy.ai/api/search-image?query=aerial%20cinematic%20view%20of%20modern%20suburban%20neighborhood%20at%20sunrise%20with%20service%20vans%20parked%20on%20streets%2C%20warm%20golden%20hour%20lighting%2C%20clean%20modern%20homes%2C%20professional%20commercial%20photography%2C%20bright%20and%20airy%20color%20palette%2C%20photorealistic&width=1920&height=1080&seq=hero-light-01&orientation=landscape"
            }
            alt="PureHomeScale AI Growth Platform"
            className="w-full h-full object-cover object-center"
          />
          {/* Multi-layer gradient overlay */}
          <div className={`absolute inset-0 ${isDark 
            ? 'bg-gradient-to-b from-[#060A14]/70 via-[#060A14]/40 to-[#060A14]' 
            : 'bg-gradient-to-b from-[#F8FAFC]/80 via-[#F8FAFC]/40 to-[#F8FAFC]'
          }`}></div>
          <div className={`absolute inset-0 ${isDark 
            ? 'bg-gradient-to-r from-[#060A14]/80 via-transparent to-[#060A14]/30'
            : 'bg-gradient-to-r from-[#F8FAFC]/90 via-transparent to-[#F8FAFC]/50'
          }`}></div>
        </div>

        {/* Subtle noise texture overlay */}
        <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")'}}></div>

        {/* Radial glow */}
        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[600px] rounded-full blur-[120px] pointer-events-none transition-colors duration-500 ${isDark ? 'bg-[#2563EB]/10' : 'bg-[#2563EB]/5'}`}></div>
        <div className={`absolute top-1/3 left-1/3 w-[400px] h-[400px] rounded-full blur-[80px] pointer-events-none transition-colors duration-500 ${isDark ? 'bg-[#06B6D4]/8' : 'bg-[#06B6D4]/4'}`}></div>

        {/* Content */}
        <div className="relative z-10 flex-1 flex items-center justify-center pt-20">
          <div className="max-w-5xl mx-auto px-6 md:px-10 w-full py-24 md:py-32 text-center">

            {/* Eyebrow label */}
            <div
              className={`inline-flex items-center gap-2 backdrop-blur-sm border rounded-full px-5 py-2 mb-10 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'} ${isDark ? 'bg-white/8 border-white/12' : 'bg-white/60 border-[#E2E8F0]'}`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#06B6D4] animate-pulse flex-shrink-0"></span>
              <span className={`text-xs font-medium tracking-[0.15em] uppercase font-inter ${isDark ? 'text-white/70' : 'text-[#334155]'}`}>AI-Powered Growth for Home Service Businesses</span>
            </div>

            {/* Headline */}
            <h1
              className={`text-6xl md:text-8xl font-black leading-[1.0] tracking-[-0.03em] mb-8 font-poppins transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${isDark ? 'text-white' : 'text-[#0F172A]'}`}
            >
              Stop Missing Jobs.<br />
              <span className="bg-gradient-to-r from-[#F97316] via-[#fb923c] to-[#06B6D4] bg-clip-text text-transparent">
                Start Scaling Revenue.
              </span>
            </h1>

            {/* Subheadline */}
            <p
              className={`text-xl md:text-2xl leading-relaxed max-w-2xl mx-auto mb-12 font-inter font-light transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${isDark ? 'text-white/55' : 'text-[#334155]/70'}`}
            >
              PureHomeScale installs AI-powered systems that capture every lead, respond instantly, and turn inbound demand into booked jobs — automatically.
            </p>

            {/* CTAs */}
            <div
              className={`flex flex-col sm:flex-row gap-4 justify-center items-center transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            >
              <button
                onClick={() => setModalOpen(true)}
                className={`group font-semibold text-base px-8 py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap font-inter flex items-center gap-2.5 ${isDark ? 'bg-white text-[#0F172A] hover:bg-white/90' : 'bg-[#0F172A] text-white hover:bg-[#1e293b]'}`}
              >
                Book Demo
                <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform duration-200"></i>
              </button>
              <a
                href="#how-it-works"
                className={`group font-medium text-base px-8 py-4 rounded-full border transition-all duration-300 cursor-pointer whitespace-nowrap text-center font-inter flex items-center gap-2 ${isDark ? 'text-white/70 border-white/15 hover:border-white/30 hover:text-white hover:bg-white/5' : 'text-[#334155] border-[#E2E8F0] hover:border-[#334155]/30 hover:text-[#0F172A] hover:bg-[#0F172A]/5'}`}
              >
                See How It Works
                <i className="ri-play-circle-line text-lg opacity-60 group-hover:opacity-100 transition-opacity"></i>
              </a>
            </div>

            {/* Social proof strip */}
            <div
              className={`mt-16 flex flex-col sm:flex-row items-center justify-center gap-6 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            >
              <div className="flex -space-x-2.5">
                {[1,2,3,4,5].map((i) => (
                  <div key={i} className={`w-9 h-9 rounded-full border-2 overflow-hidden ${isDark ? 'border-[#060A14]' : 'border-[#F8FAFC]'}`}>
                    <img
                      src={`https://readdy.ai/api/search-image?query=professional%20headshot%20portrait%20of%20home%20service%20business%20owner%2C%20confident%20smile%2C%20neutral%20background%2C%20studio%20lighting%2C%20photorealistic&width=72&height=72&seq=social-proof-${i}&orientation=squarish`}
                      alt="Client"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                ))}
              </div>
              <div className="text-center sm:text-left">
                <div className="flex items-center gap-1 justify-center sm:justify-start mb-1">
                  {[1,2,3,4,5].map((s) => (
                    <i key={s} className="ri-star-fill text-[#F97316] text-xs"></i>
                  ))}
                  <span className={`text-xs ml-1 font-inter ${isDark ? 'text-white/50' : 'text-[#334155]/60'}`}>5.0</span>
                </div>
                <p className={`text-xs font-inter ${isDark ? 'text-white/35' : 'text-[#334155]/50'}`}>Trusted by 200+ home service operators</p>
              </div>
              <div className={`hidden sm:block w-px h-8 ${isDark ? 'bg-white/10' : 'bg-[#E2E8F0]'}`}></div>
              <div className="flex items-center gap-4">
                {[{ val: '37%', lbl: 'More Bookings' }, { val: '10x', lbl: 'Faster Response' }, { val: '30d', lbl: 'To ROI' }].map((s) => (
                  <div key={s.lbl} className="text-center">
                    <div className={`font-bold text-sm font-poppins ${isDark ? 'text-white' : 'text-[#0F172A]'}`}>{s.val}</div>
                    <div className={`text-xs font-inter ${isDark ? 'text-white/30' : 'text-[#334155]/50'}`}>{s.lbl}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom fade */}
        <div className={`absolute bottom-0 left-0 right-0 h-32 pointer-events-none transition-colors duration-500 ${isDark ? 'bg-gradient-to-t from-[#060A14] to-transparent' : 'bg-gradient-to-t from-[#F8FAFC] to-transparent'}`}></div>

        {/* Scroll cue */}
        <div className="relative z-10 flex justify-center pb-10">
          <div className={`flex flex-col items-center gap-2 transition-opacity duration-500 ${isDark ? 'opacity-30' : 'opacity-40'}`}>
            <div className={`w-5 h-8 rounded-full border flex items-start justify-center pt-1.5 ${isDark ? 'border-white/40' : 'border-[#334155]/40'}`}>
              <div className={`w-1 h-2 rounded-full ${isDark ? 'bg-white' : 'bg-[#334155]'}`}></div>
            </div>
          </div>
        </div>
      </section>

      <BookDemoModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
}